import { api } from '@/portal-api.definition';
import { useNosIdQuery, useNosIdMutation } from '@/hooks/api/base.hooks';
import { ActionType, ImageProfile, mage } from '@nosinovacao/nosid-mfe-common';
import { queryOptions, useQueryClient } from '@tanstack/react-query';

export function userApplicationsQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ['user-applications', userId],
    queryFn: () =>
      api
        .get('/api/v1/applications/:secondaryUserId', { params: { secondaryUserId: userId } })
        .then((apps) =>
          apps
            .map((app) => ({ ...app, LogoUrl: mage.getImage(app.LogoUrl, ImageProfile.App) }))
            .sort((app1, app2) => {
              const app1CanToggle = app1.IsAuthorized || app1.CanRequestAccess;
              const app2CanToggle = app2.IsAuthorized || app2.CanRequestAccess;
              if (app1CanToggle === app2CanToggle) return 0;
              return app1CanToggle ? -1 : 1;
            }),
        ),
  });
}

export function useGetUserApplications(userId: string) {
  return useNosIdQuery(userApplicationsQueryOptions(userId));
}

export function useToggleAppPermission() {
  const queryClient = useQueryClient();
  return useNosIdMutation({
    mutationFn: ({
      appId,
      userId,
      isEnabled,
    }: {
      appId: string;
      userId: string;
      isEnabled: boolean;
    }) =>
      api.post('/api/v1/applications/permission/:appId/user/:userId', undefined, {
        params: { appId, userId },
        search: { isEnabled },
      }),
    successLogAction: ({ appId }) => ({ ActionType: ActionType.Submit, Option: appId }),
    errorLogAction: ({ appId }) => ({ ActionType: ActionType.Submit, Option: appId }),
    onSuccess: (_result, input) => {
      queryClient.setQueryData(userApplicationsQueryOptions(input.userId).queryKey, (prev) =>
        (prev ?? []).map((app) =>
          app.Id === input.appId ? { ...app, IsAuthorized: input.isEnabled } : app,
        ),
      );
    },
  });
}
